'use client';

import { memo, useEffect, useMemo, useState } from 'react';

import { formatUnits } from 'viem';
import { useChainId, useReadContract } from 'wagmi';

import { poolContractAbi } from '@/abi';
import PoolsTokenInfo from '@/app/[locale]/(main)/(home)/_components/Pools/PoolsTokenInfo';
import ChartSection from '@/components/shared/ChartSection';
import { cn } from '@/lib/utils/cn';
import {
  getPoolPriceHistoryAction,
  I_PoolPriceHistoryItem
} from '@/server/actions/pools/getPoolPriceHistory.action';
import { C_DEFAULT_POOL_TIMEFRAME } from '@/ts/constants/C_CHART_DEFAULTS';
import { C_ACTIVE_NETWORK_ID } from '@/ts/constants/C_NETWORKS';
import { C_USDC_DECIMALS } from '@/ts/constants/C_USDC_DECIMALS';
import { T_LinePoint, T_Symbol, T_Timeframe } from '@/ts/types/T_Chart';
import { T_Pool } from '@/ts/types/T_Pool';

import type { I_MarketData } from '@/server/actions/market/getHistory.action';
import type { Address } from 'viem';

interface I_PoolsAfterSaleTabProps {
  token: T_Pool;
  className?: string;
  isModal?: boolean;
  closeModal?: () => void;
}

export const PoolsAfterSaleTab = memo(
  ({
    token,
    className = '',
    isModal,
    closeModal
  }: I_PoolsAfterSaleTabProps) => {
    const [timeframe, setTimeframe] = useState<T_Timeframe>(
      C_DEFAULT_POOL_TIMEFRAME
    );
    const [priceHistoryData, setPriceHistoryData] = useState<{
      currentPriceDecimal: string;
      priceHistory: I_PoolPriceHistoryItem[];
    } | null>(null);
    const chainId = useChainId();

    useEffect(() => {
      const fetchHistory = async () => {
        try {
          const response = await getPoolPriceHistoryAction(
            token.poolAddress,
            timeframe
          );

          setPriceHistoryData(response.data);
        } catch (error) {
          console.error('Failed to fetch pool price history', error);
          setPriceHistoryData({
            currentPriceDecimal: '0',
            priceHistory: []
          });
        }
      };

      fetchHistory();
    }, [token.poolAddress, timeframe]);

    const activeChainId = chainId || C_ACTIVE_NETWORK_ID;

    // Process price history
    const processedHistory: T_LinePoint[] = useMemo(() => {
      if (!priceHistoryData?.priceHistory) return [];
      return priceHistoryData.priceHistory.map(item => {
        const timestampMicroseconds = parseInt(item.timestamp);
        const timestampSeconds = Math.floor(timestampMicroseconds / 1000000);
        const priceValue = parseFloat(item.priceDecimal) || 0;
        return {
          value: priceValue,
          time: timestampSeconds
        };
      });
    }, [priceHistoryData]);

    const historyWithFallback = useMemo(() => {
      if (processedHistory.length === 0 && token.presaleTokenPriceInUSDC) {
        const presalePrice = parseFloat(token.presaleTokenPriceInUSDC);
        if (!isNaN(presalePrice)) {
          return [
            {
              time: Math.floor(Date.now() / 1000),
              value: presalePrice
            }
          ];
        }
      }
      return processedHistory;
    }, [processedHistory, token.presaleTokenPriceInUSDC]);

    // Calculate prices for change logic
    const oldPrice = useMemo(() => {
      if (historyWithFallback.length > 1 && timeframe !== 'ALL') {
        return historyWithFallback[0].value;
      }

      return parseFloat(token.presaleTokenPriceInUSDC) || 0;
    }, [historyWithFallback, timeframe, token.presaleTokenPriceInUSDC]);

    const initialNewPrice = useMemo(() => {
      if (historyWithFallback.length > 0) {
        return historyWithFallback[historyWithFallback.length - 1].value;
      }
      return oldPrice;
    }, [historyWithFallback, oldPrice]);

    // Check if we need to fetch from contract
    const shouldFetchFromContract = useMemo(() => {
      return (
        oldPrice === initialNewPrice && oldPrice > 0 && initialNewPrice > 0
      );
    }, [oldPrice, initialNewPrice]);

    // Fetch price from contract if needed
    const { data: contractTokenPrice } = useReadContract({
      address: token?.poolAddress as Address | undefined,
      abi: poolContractAbi,
      functionName: 'getTokenPrice',
      chainId: activeChainId,
      query: {
        enabled: shouldFetchFromContract && !!token?.poolAddress
      }
    }) as { data: bigint | undefined };

    // Construct marketData
    const marketData: I_MarketData = useMemo(() => {
      let currentPriceDecimal =
        priceHistoryData?.currentPriceDecimal ||
        token.blockchainData?.currentPriceDecimal ||
        '0';
      let calculatedNewPrice = initialNewPrice;

      if (shouldFetchFromContract && contractTokenPrice !== undefined) {
        const contractPrice = formatUnits(contractTokenPrice, C_USDC_DECIMALS);
        calculatedNewPrice = parseFloat(contractPrice);
        currentPriceDecimal = contractPrice;
      }

      const changedPrice = calculatedNewPrice - oldPrice;
      const changedPercent =
        oldPrice > 0 ? ((changedPrice / oldPrice) * 100).toFixed(4) : '0';

      return {
        history: historyWithFallback,
        tickerPrice: {
          symbol: token.tokenSymbol,
          price: currentPriceDecimal !== '0' ? currentPriceDecimal : '0',
          priceChange: `${changedPrice}`,
          priceChangePercent: changedPercent,
          time: Date.now()
        }
      };
    }, [
      priceHistoryData,
      token,
      historyWithFallback,
      initialNewPrice,
      oldPrice,
      shouldFetchFromContract,
      contractTokenPrice
    ]);

    return (
      <div
        className={cn(
          'bg-background gap-0 rounded-bl-2xl rounded-br-2xl',
          isModal ? 'pb-13' : '',
          className
        )}
      >
        <div className="w-full flex min-h-100">
          <ChartSection
            url="/pools"
            symbol={token.tokenSymbol as T_Symbol}
            marketData={marketData}
            timeframe={timeframe}
            type="pool"
            variant="modal"
            poolDecimals={token.decimals}
            pool={token}
            setStateTimeframe={setTimeframe}
          />
        </div>
        <PoolsTokenInfo
          token={token}
          closeModal={closeModal}
          isModal={isModal}
        />
      </div>
    );
  }
);

PoolsAfterSaleTab.displayName = 'PoolsAfterSaleTab';
